import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, User, Loader2, Instagram, Youtube, Twitter } from 'lucide-react';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_creator-brand-2/artifacts/feyyw8lo_145437-200.png";

// Network Visualization Component
const NetworkVisualization = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-60">
    <svg className="w-full h-full" viewBox="0 0 800 600">
      <defs>
        <linearGradient id="lineGradLogin" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#F472B6" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <g className="opacity-40">
        <line x1="100" y1="100" x2="300" y2="200" stroke="url(#lineGradLogin)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="300" y1="200" x2="500" y2="150" stroke="url(#lineGradLogin)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="500" y1="150" x2="700" y2="250" stroke="url(#lineGradLogin)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="200" y1="400" x2="400" y2="350" stroke="url(#lineGradLogin)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="400" y1="350" x2="600" y2="450" stroke="url(#lineGradLogin)" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="16" dur="2s" repeatCount="indefinite"/>
        </line>
      </g>
      <g>
        <circle cx="100" cy="100" r="5" fill="#F472B6" opacity="0.6">
          <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="300" cy="200" r="6" fill="#14B8A6" opacity="0.7">
          <animate attributeName="r" values="6;8;6" dur="2.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="500" cy="150" r="5" fill="#FB923C" opacity="0.6">
          <animate attributeName="r" values="5;7;5" dur="1.8s" repeatCount="indefinite"/>
        </circle>
        <circle cx="700" cy="250" r="6" fill="#F472B6" opacity="0.6">
          <animate attributeName="r" values="6;8;6" dur="2.2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="200" cy="400" r="4" fill="#14B8A6" opacity="0.5">
          <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="400" cy="350" r="7" fill="#FB923C" opacity="0.7">
          <animate attributeName="r" values="7;9;7" dur="2.3s" repeatCount="indefinite"/>
        </circle>
        <circle cx="600" cy="450" r="5" fill="#F472B6" opacity="0.6">
          <animate attributeName="r" values="5;7;5" dur="1.9s" repeatCount="indefinite"/>
        </circle>
      </g>
    </svg>
  </div>
);

// Wave Background
const WaveBackground = () => (
  <div className="absolute bottom-0 left-0 right-0 h-[300px] overflow-hidden pointer-events-none">
    <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1440 300" preserveAspectRatio="none">
      <defs>
        <linearGradient id="waveGradLogin1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F472B6" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#FB923C" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#14B8A6" stopOpacity="0.2" />
        </linearGradient>
        <linearGradient id="waveGradLogin2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#F472B6" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <path fill="url(#waveGradLogin1)" d="M0,150 C300,220 600,80 900,150 C1200,220 1350,100 1440,150 L1440,300 L0,300 Z">
        <animate attributeName="d" dur="10s" repeatCount="indefinite" values="
          M0,150 C300,220 600,80 900,150 C1200,220 1350,100 1440,150 L1440,300 L0,300 Z;
          M0,180 C300,100 600,220 900,140 C1200,60 1350,200 1440,130 L1440,300 L0,300 Z;
          M0,150 C300,220 600,80 900,150 C1200,220 1350,100 1440,150 L1440,300 L0,300 Z"
        />
      </path>
      <path fill="url(#waveGradLogin2)" d="M0,200 C400,140 800,260 1200,200 C1350,180 1400,220 1440,210 L1440,300 L0,300 Z">
        <animate attributeName="d" dur="8s" repeatCount="indefinite" values="
          M0,200 C400,140 800,260 1200,200 C1350,180 1400,220 1440,210 L1440,300 L0,300 Z;
          M0,220 C400,280 800,160 1200,230 C1350,250 1400,180 1440,240 L1440,300 L0,300 Z;
          M0,200 C400,140 800,260 1200,200 C1350,180 1400,220 1440,210 L1440,300 L0,300 Z"
        />
      </path>
    </svg>
  </div>
);

// Floating Social Icon
const FloatingSocialIcon = ({ Icon, className, delay = 0 }) => (
  <div 
    className={`absolute p-3 rounded-xl bg-white/80 shadow-lg backdrop-blur-sm animate-float ${className}`}
    style={{ animationDelay: `${delay}s` }}
  >
    <Icon className="w-5 h-5 text-[#E8795A]" />
  </div>
);

// Decorative Star
const DecorativeStar = ({ className, color = '#F472B6', size = 10 }) => (
  <svg className={`absolute animate-pulse ${className}`} width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41L12 0Z" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(searchParams.get('role') || 'creator');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(email, password);
        toast.success('Welcome back!');
        
        if (!user.onboarding_completed) {
          navigate('/onboarding');
        } else {
          navigate(user.role === 'creator' ? '/creator' : '/brand');
        }
      } else {
        if (!name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        const user = await register(email, password, name, selectedRole);
        toast.success('Account created!');
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/onboarding';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent('CmdOut.io')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFBFC] to-white relative overflow-hidden">
      {/* Creative Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Vibrant Orbs */}
        <div className="creator-orb creator-orb-pink" style={{ top: '-5%', right: '5%' }} />
        <div className="creator-orb creator-orb-orange" style={{ bottom: '10%', left: '-5%' }} />
        <div className="creator-orb creator-orb-teal" style={{ top: '30%', right: '-8%' }} />
        
        {/* Network Lines */}
        <NetworkVisualization />
        
        {/* Decorative Stars */}
        <DecorativeStar className="top-[10%] left-[10%]" color="#F472B6" size={14} />
        <DecorativeStar className="top-[20%] right-[15%]" color="#FB923C" size={10} />
        <DecorativeStar className="bottom-[25%] left-[20%]" color="#14B8A6" size={12} />
        <DecorativeStar className="top-[50%] right-[10%]" color="#F472B6" size={8} />
      </div>
      
      {/* Flowing Waves */}
      <WaveBackground />

      {/* Floating Social Icons */}
      <FloatingSocialIcon Icon={Instagram} className="top-[15%] left-[5%] hidden lg:block" delay={0} />
      <FloatingSocialIcon Icon={Youtube} className="top-[30%] right-[8%] hidden lg:block" delay={0.5} />
      <FloatingSocialIcon Icon={Twitter} className="bottom-[30%] left-[8%] hidden lg:block" delay={1} />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Glass Card */}
          <div className="glass-card rounded-3xl p-8 shadow-2xl">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </button>

            {/* Logo */}
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="CmdOut" className="w-12 h-12" />
              <div>
                <span className="font-bold text-xl tracking-tight">CmdOut</span>
                <span className="text-[#3AAFB9] font-semibold">.io</span>
              </div>
            </div>

            <h1 className="text-2xl font-bold mb-2">
              {isLogin ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-muted-foreground mb-6 text-sm">
              {isLogin ? 'Sign in to your CmdOut dashboard' : 'Join the creator-brand marketplace'}
            </p>

            {/* Role Selection for Signup */}
            {!isLogin && (
              <div className="mb-5">
                <Label className="text-sm font-medium mb-2 block">I am a</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedRole('creator')}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      selectedRole === 'creator' 
                        ? 'border-[#E8795A] bg-[#E8795A]/10' 
                        : 'border-border hover:border-[#E8795A]/50'
                    }`}
                    data-testid="role-creator-btn"
                  >
                    <span className="font-semibold block text-sm">Creator</span>
                    <span className="text-xs text-muted-foreground">Looking for brands</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRole('brand')}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      selectedRole === 'brand' 
                        ? 'border-[#3AAFB9] bg-[#3AAFB9]/10' 
                        : 'border-border hover:border-[#3AAFB9]/50'
                    }`}
                    data-testid="role-brand-btn"
                  >
                    <span className="font-semibold block text-sm">Brand</span>
                    <span className="text-xs text-muted-foreground">Looking for creators</span>
                  </button>
                </div>
              </div>
            )}

            {/* Google Auth */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 mb-5 border-2 hover:bg-secondary"
              onClick={handleGoogleAuth}
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white/80 px-3 text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-sm font-medium mb-1 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-11 border-2"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-sm font-medium mb-1 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 border-2"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium mb-1 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 border-2"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 rounded-xl bg-gradient-to-r from-[#14B8A6] to-[#3AAFB9] hover:shadow-lg hover:shadow-[#14B8A6]/30 font-semibold transition-all"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <p className="text-center mt-5 text-sm text-muted-foreground">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#3AAFB9] hover:underline font-semibold"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
